import React from "react";
import { useTranslation } from "react-i18next";
import { RoomMovie } from "../../../api/room";
import { formatLikeCount, movieTitleHandler } from "../../../utils/helpers";
import styles from "./RoomMovieInfo.module.scss";
import {genresData} from "../../../constants/structure";
import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import LikeIcon from "../../../components/icons/chat/likeIcon";
import BookMarkIcon from "../../../components/icons/chat/bookMarkIcon";
import ShareIcon from "../../../components/icons/chat/shareIcon";
import {ROUTES} from "../../../constants/routes";
import { useNavigate } from "react-router-dom";

interface RoomMovieInfoProps {
  movie: RoomMovie;
  onLike: () => void;
  onDislike: () => void;
  onShare: () => void;
  isLiked: boolean;
  isSwiping: boolean;
}

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

const getPosterPath = (lng: string, movie: RoomMovie): string => {
  if (lng === "ru" && movie.poster_path_ru) return movie.poster_path_ru;
  if (lng === "uk" && movie.poster_path_ua) return movie.poster_path_ua;
  if (lng === "ar" && movie.poster_path_ar) return movie.poster_path_ar;
  return movie.poster_path;
};

const RoomMovieInfo: React.FC<RoomMovieInfoProps> = ({ movie, onLike, onShare, isLiked, isSwiping }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const title = movieTitleHandler(i18n.language, movie) || movie.title;
  const poster = getPosterPath(i18n.language, movie);
  const year = movie.release_date?.slice(0, 4);

  const genreList = movie.genres
    ? JSON.parse(movie.genres)
      .slice(0, 2)
      .map((id: number) => genresData.find((genre) => genre.id === id)?.name)
      .filter(Boolean)
      .map((name: string) => t(name))
    : [];

  const imdbRating = movie ? (movie.imdb_rating?.toFixed(1) || movie.vote_average?.toFixed(1)) : null;
  const tomatoRating = movie ? (movie.rotten_tomatoes_rating?.toFixed(0)) : null;

  return (
    <>
      <div className={styles.infoBar}>
        <div className={styles.infoDescription}>
          <div className={styles.posterWrap}>
            {poster ? (
              <img src={`${IMG_BASE}${poster}`} alt={title} className={styles.poster} />
            ) : (
              <div className={styles.posterPlaceholder}>{title}</div>
            )}
          </div>
          <div className={styles.infoBox}>
            <h3 className={styles.infoBarTitle}>{title}</h3>

            <div className={styles.meta}>
                {genreList.length > 0 && (
                  <div className={styles.genres}>
                    {genreList.join(", ")}
                  </div>
                )}
                {year && <span className={styles.year}>{year}</span>}
            </div>

            <div className={styles.cardActionWrapper}>
              {imdbRating && (
                <div className={styles.ratingItem}>
                  <OverlayRatingIcon />
                  <p className={styles.rating}>{imdbRating}</p>
                </div>
              )}
              {tomatoRating && (
                <div className={styles.ratingItem}>
                  <OverlayTomatoRatingIcon />
                  <p className={styles.rating}>{tomatoRating}</p>
                </div>
              )}
            </div>
          </div>

        </div>


        <div className={styles.actions}>

          <div
            className={`${styles.actionsItem} ${isLiked ? styles.actionsItemActive : ""}`}
            onClick={!isSwiping && !isLiked ? onLike : undefined}
          >
            <div className={styles.actionsIcon}><LikeIcon/></div>
            <div className={styles.actionsValue}>{formatLikeCount(movie.likes_count, { compact: true })}</div>
          </div>

          <div
            className={styles.actionsItem}
          >
            <div className={styles.actionsIcon}><BookMarkIcon/></div>
            <div className={styles.actionsValue}>{formatLikeCount(movie.favorites_count, { compact: true })}</div>
          </div>

          <div
            className={styles.actionsItem}
            onClick={onShare}
          >
            <div className={styles.actionsIcon}><ShareIcon /></div>
            <div className={styles.actionsValue}>{formatLikeCount(movie.shares_count, { compact: true })}</div>
          </div>
        </div>

        <div className={styles.actionsButtons}>
          <button
            className={styles.actionsButton}
            onClick={() => navigate(`${ROUTES.DETAILS}/${movie.id}`)}
          >
            <span>{t("room_details", "Details")}</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default RoomMovieInfo;
